<script lang="ts" setup>
import { OtLozenge, OtIcon, OtNumberDisplay } from '@1trading/design-system';
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import type { NormalizedPerpsPosition } from '@exchange/libs/account/service/src/lib/wss-account-messages';

import DivTableCell from '../components/DivTableCell.vue';
import DivTableRow from '../components/DivTableRow.vue';
import usePositionComputed from '../usePositionComputed';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
  position: {
    type: Object as PropType<NormalizedPerpsPosition>,
    required: true,
  },
  maxLeverage: {
    type: Number,
    required: false,
  },
});

const emit = defineEmits(['close-position']);

const { position, maxLeverage } = toRefs(props);
const { marginUsed, market, unrealizedPnL } = usePositionComputed(position, maxLeverage);

const directionText = computed(() => (props.position.direction === 'LONG' ? t('fundamentals.orders.side.long') : t('fundamentals.orders.side.short')));
const directionClass = computed(() => (props.position.direction === 'LONG' ? 'direction-long' : 'direction-short'));
</script>

<template>
  <div-table-row
    class="positions-item"
    :data-id="position.instrument_code"
  >
    <div-table-cell class="positions-item__cell">
      <!-- Market -->
      {{ market?.name }}
      <ot-lozenge
        :filled="false"
        :text="$t('fundamentals.perpetual.short')"
        class="lozenge"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Direction -->
      <span :class="directionClass">{{ directionText }}</span>
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Size -->
      <ot-number-display
        :number="position.open_volume"
        :precision="market?.amountPrecision"
        :suffix="market?.base.code"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Position Value -->
      <ot-number-display
        :number="position.open_position"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Average Price -->
      <ot-number-display
        :number="position.average_entry_price"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Mark Price -->
      <ot-number-display
        :number="position.mark_price"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Last Settlement -->
      <ot-number-display
        :number="position.last_settlement"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
        :class="{ 'profit-positive': Number(position.last_settlement) > 0, 'profit-negative': Number(position.last_settlement) < 0 }"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Realised PNL -->
      <ot-number-display
        :number="position.realised_pnl"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
        :class="{ 'profit-positive': Number(position.realised_pnl) > 0, 'profit-negative': Number(position.realised_pnl) < 0 }"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Unrealised PNL -->
      <ot-number-display
        :number="unrealizedPnL"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
        :class="{
          'profit-positive': unrealizedPnL > 0,
          'profit-negative': unrealizedPnL < 0,
        }"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <!-- Margin Used -->
      <ot-number-display
        :number="marginUsed"
        :precision="market?.marketPrecision"
        :suffix="market?.quote.code"
      />
    </div-table-cell>
    <div-table-cell class="positions-item__cell">
      <button
        class="position-close-button"
        name="close-position-button"
        testid="close-position-button"
        @click="() => emit('close-position', position)"
      >
        <ot-icon name="close-square" />
      </button>
    </div-table-cell>
  </div-table-row>
</template>

<style lang="scss">
.positions-item {
  height: 44px;
  border-radius: var(--app-border-radius);
  color: rgb(var(--v-theme-text-secondary));
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);

  &__cell {
    display: flex;
    max-width: 120px;
    align-items: center;
    white-space: nowrap;
  }

  &__close {
    width: 60px;
  }

  &__flip {
    width: 100px; // Adjust as needed
  }
}

.lozenge {
  margin-left: 4px;
}

.direction-long {
  color: rgb(var(--v-theme-success));
}

.direction-short {
  color: rgb(var(--v-theme-error));
}

.position-close-button {
  display: flex;
  min-width: auto;
  height: auto;
  padding: 0;
  border-radius: 2px;
  color: rgb(var(--v-theme-text-primary));

  .svg-icon--close-square {
    .svg-icon {
      &__content {
        fill: rgb(var(--v-theme-text-secondary));
      }

      &__border {
        fill: rgb(var(--v-theme-elevation-2));
      }
    }
  }

  &:hover {
    .svg-icon--close-square .svg-icon__bg {
      fill: rgb(var(--v-theme-elevation-2));
      transition: 0.2s;
    }
  }
}

.profit-positive {
  color: rgb(var(--v-theme-success));
}

.profit-negative {
  color: rgb(var(--v-theme-error));
}
</style>
